import { Component, Inject, Optional, ViewEncapsulation } from '@angular/core';
import {
    MAT_DIALOG_DATA,
    MatDialogRef
} from '@angular/material/dialog';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { combineLatest, Observable, of } from 'rxjs';
import { IProduct, Product } from '@shared/models/product';
import { debounceTime, map, startWith, switchMap } from 'rxjs/operators';
import { U2bValidators } from '@shared/validators/validators';
import { U2bProductValidators } from '@shared/validators/product/product-validators';
import { ProductsApiService } from '@modules/bcm/@shared/services';
import { AppNotificationService } from '@core/services/app-notification.service';
import { U2bNumericValidators } from '@shared/validators/numeric';
import { isString } from '@shared/functions/is-string';
import { DEFAULT_DEBOUNCE_TIME } from '@modules/bcm/@shared/constants';
import { TenantRelationWithDisable } from '@shared/models/tenant-relation';
import { TolgeeService } from '@core/translation/tolgee.service';
import { isElectricMeterProductCategory } from '@shared/constants/electric-meter-product-category';
import { isDayOrOvernightProductUnit } from '@shared/utils/berth-product-booking.util';
import { resolveUnitTranslationKey } from '@shared/functions/resolve-unit-translation-key';
import { IUnit, UnitUniqueName } from '@shared/models/unit';
import { formatProductUnitLabel } from '@modules/bcm/electric-meters/readings/reading-dialog/reading-dialog-product-display';

@Component({
    selector: 'add-position-dialog',
    templateUrl: './get-product-dialog.component.html',
    styleUrls: ['./get-product-dialog.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class GetProductDialogComponent {
    productForm: UntypedFormGroup;
    price: number;
    totalPrice: number;
    products: Product[];
    filteredProducts$: Observable<Product[]>;
    isSaving: boolean;
    showQuantityField = true;
    categories: string[];
    categoryIds: number[];
    showIgnoreFreeOfChargePositionCheckbox: boolean;
    showBookingSettingsSection: boolean;
    showSkipStayDurationQuantityCheckbox = false;
    product: Product;
    quantityZeroAllowed = true;

    constructor(
        public dialogRef: MatDialogRef<GetProductDialogComponent>,
        @Optional() @Inject(MAT_DIALOG_DATA) protected data: {
            showQuantityField?: boolean,
            categories?: string[],
            categoryIds?: number[],
            product?: Product,
            showIgnoreFreeOfChargePositionCheckbox?: boolean,
            quantityZeroAllowed?: boolean,
            hasTenantRelationSelection?: boolean,
            // Set only by the tenant-relation form: that quantity is master data shared by every
            // assignment of the relation, so an m² product cannot pick up a boat's area there. (#6944)
            warnAboutFixedSqmQuantity?: boolean,
        },
        private _formBuilder: UntypedFormBuilder,
        private _productApiService: ProductsApiService,
        private _appNotificationService: AppNotificationService,
        private _tolgee: TolgeeService,
    ) {
        dialogRef.addPanelClass('get-product-dialog');

        this.product = data?.product;
        this.showIgnoreFreeOfChargePositionCheckbox = data?.showIgnoreFreeOfChargePositionCheckbox;
        this.showBookingSettingsSection = !!this.showIgnoreFreeOfChargePositionCheckbox;
        this.showQuantityField = data?.showQuantityField !== false;
        this.categories = Array.isArray(data?.categories) ? data.categories : [];
        this.categoryIds = Array.isArray(data?.categoryIds) ? data.categoryIds : [];
        this.productForm = this._createForm();

        if (this.data?.product) {
            this.showSkipStayDurationQuantityCheckbox = isDayOrOvernightProductUnit(this.data.product);
        }

        if (data.quantityZeroAllowed === false) {
            this.quantityZeroAllowed = false;
        }

        const form = this.productForm;
        const productField = form.get('product');
        const quantityField = form.get('quantity');
        let lastSelectedProductId: number | undefined = this.data?.product?.id;

        productField.valueChanges.subscribe((value) => {
            if (isString(value) || !value) {
                this.showSkipStayDurationQuantityCheckbox = false;
                return;
            }

            const product = value as Product;
            this.showSkipStayDurationQuantityCheckbox = isDayOrOvernightProductUnit(product);

            if (!quantityField.enabled) {
                return;
            }

            const defaultQuantity = resolveQuantityOnProductSelection(
                lastSelectedProductId,
                product.id,
                quantityField.value,
            );

            if (defaultQuantity != null && defaultQuantity !== quantityField.value) {
                quantityField.setValue(defaultQuantity);
            }

            lastSelectedProductId = product.id;
        });

        combineLatest([productField.valueChanges, quantityField.valueChanges])
            .subscribe(
                (values) => this._calculateTotalPrice((values[0] as Product || {price: 0}).price, values[1])
            );

        this._loadProducts();

        this.filteredProducts$ = this.productForm.get('product').valueChanges
            .pipe(
                startWith(this.products),
                debounceTime(DEFAULT_DEBOUNCE_TIME),
                switchMap((value) => isString(value) ? this._filterProducts(value) : of(this.products))
            );
    }

    tenantRelationDisableConditionCallback = (selectedItems: TenantRelationWithDisable[], allItems: TenantRelationWithDisable[]) => {
        allItems.forEach(currentItem => {
            const isItemSelected = selectedItems.some(item => item.id === currentItem.id);

            currentItem.disabled = isItemSelected;

            if (isItemSelected) {
                currentItem.disabledReason = 'Bereits selektiert.';
            }
        });
    };

    public displayProductWith = (product: IProduct | string): string => {
        if (!product || typeof product === 'string') {
            return typeof product === 'string' ? product : '';
        }
        return product.name || '';
    };

    onProductSelected(event: MatAutocompleteSelectedEvent): void {
        const product = event.option.value as Product;
        const productField = this.productForm.get('product');
        productField.setValue(product);
        productField.markAsDirty();
        productField.markAsTouched();
        productField.updateValueAndValidity();
    }

    getUnitTranslationKey(unit: IUnit | null | undefined): string | null {
        return resolveUnitTranslationKey(unit);
    }

    getUnitLabel(unit: IUnit | null | undefined): string {
        return formatProductUnitLabel(unit, key => this._tolgee.translate(key));
    }

    get selectedProduct(): Product | null {
        const value = this.productForm?.get('product')?.value;
        return value?.id ? value as Product : null;
    }

    get showSqmFixedQuantityHint(): boolean {
        return !!this.data?.warnAboutFixedSqmQuantity
            && this.selectedProduct?.unit?.uniqueName === UnitUniqueName.SQM;
    }

    saveAndContinue(): void {
        if (this.productForm.invalid) {
            this.productForm.markAllAsTouched();
            this._appNotificationService.showError(this._tolgee.translate('common.checkFieldsHighlight'));
            return;
        }

        const {product, quantity, ignoreFreeOfChargePosition, skipStayDurationQuantity} = this.productForm.value;

        this.isSaving = true;
        this.dialogRef.close({
            product,
            quantity,
            ignoreFreeOfChargePosition,
            skipStayDurationQuantity: !!skipStayDurationQuantity,
            tenantRelations: this.productForm.value.tenantRelationForm?.tenantRelations,
        });
    }

    private _createForm(): UntypedFormGroup {
        return this._formBuilder.group({
            product: [
                this.data?.product || '',
                [
                    U2bValidators.required(this._tolgee.translate('common.validationMsg.selectProductFromList')),
                    U2bProductValidators.productExists(),
                ],
            ],
            quantity: [
                {
                    value: this.data?.product?.quantity ?? null,
                    disabled: !this.showQuantityField
                },
                [
                    U2bValidators.required(this._tolgee.translate('common.validationMsg.quantityUnitsRequired')),
                    ...(this.quantityZeroAllowed ? [] : [U2bNumericValidators.noZeroAllowed()])
                ]
            ],
            ignoreFreeOfChargePosition: [this.data?.product?.id ? this.data?.product?.ignoreFreeOfChargePosition : false],
            skipStayDurationQuantity: [this.data?.product?.id ? this.data?.product?.skipStayDurationQuantity : false],
        });
    }

    private _calculateTotalPrice(price = 0, quantity = 0): void {
        if (price == null || quantity == null) {
            return;
        }

        this.totalPrice = (price * quantity);
    }

    private _filterProducts(filter: string): Observable<Product[]> {
        return of(this.products)
            .pipe(map((products: Product[]) => Product.filterProducts(products, filter)));
    }

    private _loadProducts(): void {
        this._productApiService
            .getAll()
            .subscribe((products: Product[]) => {
                if (this.categoryIds.length) {
                    products = products.filter(p => this.categoryIds.includes(p.category?.id));
                } else if (this.categories.length) {
                    products = products.filter(p =>
                        this.categories.includes(p.category?.name)
                        || isElectricMeterProductCategory(p.category)
                    );
                }

                this.products = products;
            });
    }
}

export function resolveQuantityOnProductSelection(
    previousProductId: number | undefined,
    selectedProductId: number | undefined,
    currentQuantity: number | string | null | undefined,
): number | null {
    if (selectedProductId == null) {
        return null;
    }

    if (previousProductId !== selectedProductId) {
        return 1;
    }

    if (currentQuantity == null || currentQuantity === '') {
        return 1;
    }

    return null;
}
